/* eslint-disable no-param-reassign */
import React, { useEffect, useReducer, useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import produce from 'immer';
import { isEqual } from 'lodash';

// HTTP
import { useMutation, useLazyQuery } from '@apollo/client';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import DealsQuery from 'services/graphQL/query/DealsQuery';
import DealsMutate from 'services/graphQL/mutate/DealsMutate';

import { FormLabel, makeStyles } from '@material-ui/core';
import GroupActionsButtons from 'components/widgets/GroupActionsButtons';

import { FetchPolicy } from 'utils/enum/Core';
import { DealSection, DealStatus } from 'utils/enum/DealEnum';
import InventoryMap from 'services/mapData/InventoryMap';
import Permission from 'utils/enum/Permissions';
import KeyStore from 'utils/KeyStore';
import { InventoryPanels } from 'utils/enum/InventoryEnum';
import ModalUtils from 'utils/ModalUtils';
import MessageUtils from 'utils/MessageUtils';
import StringUtils from 'lib/StringUtils';
import VehicleDealsContext from 'components/context/VehicleDealsContext';

import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    editPanel: {
        padding: '5px 5px 0',
        border: '1px solid #e8e8e8',
        boxShadow: '1px 2px 5px 0px #c1c1c1',
        margin: '0 20px 15px 10px',
        height: '98%',
    },
    titleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    title: {
        color: '#6b6b6b',
        fontSize: 14,
        marginBottom: 5,
        fontWeight: 'bold',
    },
});

const initState = {
    editMode: false,
    pricing: {
        purchasedPrice: 0,
        slashedPrice: 0,
        pack: 0,
        packOverride: false,
        stickerPrice: 0,
        repairs: 0,
        internetPrice: 0,
        floorplanCost: 0,
        minimumPrice: 0,
        wholeSalePrice: 0,
        age: 0,
        markup: 0,
        showSlashed: false,
    },
    bookValue: {
        nadaRetail: 0,
        nadaTrade: 0,
    },
    floorPlan: {
        flooredRate: 0,
        flooredBy: '',
        floorPlannedDate: null,
        curtailmentDate: null,
        borrowedAmount: 0,
        isFloorPlanned: false,
        isBorrowedAmountOverride: false,
        floorplanCost: 0,
        purchasedPrice: 0,
    },
    advertising: {
        enableReduce: false,
        reducePrice: '',
        reduceFrequency: '',
        removeAd: '',
        placeAd: false,
        websiteDownpayment: 0,
        websitePayment: 0,
        paymentFrequency: '',
        lastReduced: null,
        editVDPReferenceURL: false,
        vdpReferenceURL: '',
    },
    detail: {},
    customFieldsBackup: [],
    customFields: [],
    showConfirmDialog: false,
};

const ACTION_TYPES = {
    INIT_DATA: 'initData',
    ON_CHANGE: 'onChange',
    SET_OVERRIDE: 'setOverride',
    SET_EDIT_MODE: 'setEditMode',
    SET_CUSTOM_FIELDS: 'setCustomFields',
    UPDATE_CUSTOM_FIELD: 'updateCustomField',
    UPDATE_CUSTOM_FIELD_OPTIONS: 'updateCustomFieldOptions',
    ON_SAVE: 'onSave',
    ON_CHANGE_FLOORPLAN: 'onChangeFloorplan',
    UPDATE_PACK_FIELD: 'updatePackField',
    TOGGLE_CONFIRM_DIALOG: 'toggleConfirmDialog',
};

const reducer = produce((draftState, { type, payload = null }) => {
    switch (type) {
    case ACTION_TYPES.INIT_DATA:
        const { fieldName, data, isCustomFields } = payload;

        if (isCustomFields) {
            draftState.editMode = false;
            draftState.customFields = draftState.customFieldsBackup;
            return;
        }

        draftState.editMode = false;
        draftState[fieldName] = {
            ...draftState[fieldName],
            ...data,
        };
        break;
    case ACTION_TYPES.ON_CHANGE:
        draftState[payload.dataFieldName][payload.fieldName] = payload.value;
        if (payload.fieldName === 'editVDPReferenceURL' && payload.value === false) draftState[payload.dataFieldName].vdpReferenceURL = '';

        break;
    case ACTION_TYPES.ON_CHANGE_FLOORPLAN:
        draftState[payload.dataFieldName] = {
            ...draftState[payload.dataFieldName],
            ...payload.data,
        };
        break;
    case ACTION_TYPES.SET_OVERRIDE:
        draftState.pricing.packOverride = false;
        draftState.pricing.pack = payload;
        break;
    case ACTION_TYPES.SET_EDIT_MODE:
        draftState.editMode = !draftState.editMode;
        break;
    case ACTION_TYPES.SET_CUSTOM_FIELDS:
        draftState.customFieldsBackup = payload;
        draftState.customFields = payload;
        draftState.editMode = false;
        break;
    case ACTION_TYPES.UPDATE_CUSTOM_FIELD:
        draftState.customFields[payload.index].value = payload.value;
        break;
    case ACTION_TYPES.UPDATE_CUSTOM_FIELD_OPTIONS:
        draftState.customFields[payload.index].options = payload.options;
        draftState.customFields[payload.index].value = payload.value;
        break;
    case ACTION_TYPES.UPDATE_PACK_FIELD:
        const { entityMetadataId, value } = payload;
        const fields = [...(draftState.pricing.packFields ?? [])];

        draftState.pricing.packFields = fields.map((f) => {
            if (f.entityMetadataId === entityMetadataId) f.value = value;
            return f;
        });
        break;
    case ACTION_TYPES.TOGGLE_CONFIRM_DIALOG:
        draftState.showConfirmDialog = payload.forceDialogDisplay !== null ? payload.forceDialogDisplay : !draftState.showConfirmDialog;
        break;
    default:
        break;
    }
});

const getData = (title, state = {}) => {
    switch (title) {
    case InventoryPanels.PRICING:
        return state.pricing;
    case InventoryPanels.FLOORPLAN:
        return state.floorPlan;
    case InventoryPanels.BOOK_VALUE:
        return state.bookValue;
    case InventoryPanels.ADVERTISING_SETUP:
        return state.advertising;
    case InventoryPanels.PURCHASE_INFO:
        return state.detail;
    case InventoryPanels.CUSTOM_FIELDS:
        return state.customFields;
    case DealSection.DEALS_CUSTOM_FIELDS:
        return state.customFields;
    default:
        return {};
    }
};

const getFieldName = (title) => {
    switch (title) {
    case InventoryPanels.PRICING:
        return 'pricing';
    case InventoryPanels.FLOORPLAN:
        return 'floorPlan';
    case InventoryPanels.BOOK_VALUE:
        return 'bookValue';
    case InventoryPanels.ADVERTISING_SETUP:
        return 'advertising';
    case InventoryPanels.PURCHASE_INFO:
        return 'detail';
    case DealSection.DEALS_CUSTOM_FIELDS:
    case InventoryPanels.CUSTOM_FIELDS:
        return 'customFields';
    default:
        return '';
    }
};

const getMapData = (title, data) => {
    switch (title) {
    case InventoryPanels.PRICING:
        return InventoryMap.mapPricingData(data);
    case InventoryPanels.BOOK_VALUE:
        return InventoryMap.mapBookValueData(data);
    case InventoryPanels.FLOORPLAN:
        return InventoryMap.mapFloorplanData(data);
    case InventoryPanels.ADVERTISING_SETUP:
        return InventoryMap.mapAdvertisingData(data);
    default:
        return '';
    }
};

const PanelsContainers = ({
    stockNumber,
    accountNumber,
    data,
    title,
    children,
    setEditedSection,
    allowEdit,
    editedSection,
    onReload,
    blockingStatus,
    currentUserId,
    changeBlockingStatus,
    version,
}) => {
    const classes = useStyles();
    const [state, dispatch] = useReducer(reducer, initState);
    const keyStore = new KeyStore();

    const isDealsCustomFields = title === DealSection.DEALS_CUSTOM_FIELDS;
    const WRITE = keyStore.hasPermission(isDealsCustomFields ? Permission.SALES_DEAL_WRITE : Permission.INVENTORY_VEHICLE_WRITE);

    const dataFieldName = getFieldName(title);
    const isRecordBlocked = blockingStatus?.isEditing === true && blockingStatus.editingById !== currentUserId;
    const disabled = (dataFieldName !== editedSection && !StringUtils.isEmpty(editedSection)) || isRecordBlocked;
    const { dealRecords } = useContext(VehicleDealsContext) || [];

    const getValueProperty = (customFields) => customFields
        .map((cf) => ({ value: cf.value }));

    const hasChangesInOtherSections = () => {
        const current = state[dataFieldName];
        const isFloorPlan = (title === InventoryPanels.FLOORPLAN);

        const floorPlanActive = isFloorPlan && current.isFloorPlanned;
        if (floorPlanActive && StringUtils.isEmpty(current.flooredBy)) return false;
        if (floorPlanActive && !current.floorPlannedDate) return false;
        return !isEqual(data, current);
    };

    const isCustomFields = ([DealSection.DEALS_CUSTOM_FIELDS, InventoryPanels.CUSTOM_FIELDS].includes(title));
    const hasChanges = isCustomFields
        ? !isEqual(
            getValueProperty(state.customFieldsBackup),
            getValueProperty(state[dataFieldName]),
        )
        : hasChangesInOtherSections();

    const [updateVehicle, { loading }] = useMutation(InventoryMutation.UPDATE_VEHICLE);
    const [
        saveCustomFields,
        { loading: savingCustomFields },
    ] = useMutation(isDealsCustomFields ? DealsMutate.SAVE_CUSTOM_FIELDS_DEALS : InventoryMutation.SAVE_VEHICLE_CUSTOM_FIELDS, {
        onCompleted: async (response) => {
            if (response) {
                if (!isDealsCustomFields) {
                    await changeBlockingStatus({
                        variables: {
                            stockNumber,
                            isBlocking: false,
                        },
                    });
                }

                dispatch({
                    type: ACTION_TYPES.SET_CUSTOM_FIELDS,
                    payload: state.customFields,
                });

                onReload();
                ModalUtils.successMessage(null, 'Custom Fields saved successfully.');
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
        },
    });

    const [getDealsCustomFields] = useLazyQuery(DealsQuery.GET_CUSTOM_FIELDS_DEALS, {
        onCompleted: (response) => {
            if (response) {
                dispatch({
                    type: ACTION_TYPES.SET_CUSTOM_FIELDS,
                    payload: response.getCustomFieldsDeals,
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [getVehicleCustomFields] = useLazyQuery(InventoryQuery.GET_VEHICLE_CUSTOM_FIELDS, {
        onCompleted: (response) => {
            if (response) {
                dispatch({
                    type: ACTION_TYPES.SET_CUSTOM_FIELDS,
                    payload: response.getCustomFieldsInventory,
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (title === InventoryPanels.CUSTOM_FIELDS) {
            getVehicleCustomFields({
                variables: {
                    stockNumber: stockNumber.toString(),
                },
            });
        }

        if (isDealsCustomFields) {
            getDealsCustomFields({
                variables: {
                    accountNumber,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch({
            type: ACTION_TYPES.INIT_DATA,
            payload: {
                fieldName: dataFieldName,
                data,
                isCustomFields,
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, dataFieldName]);

    const getCustomFieldsInput = () => {
        const { customFields } = state;
        return customFields.map((cf) => ({
            entityMetadataId: cf.entityMetadataId,
            value: cf.value || '',
        }));
    };

    const toggleConfirmDialog = (forceDialogDisplay = null) => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_CONFIRM_DIALOG,
            payload: {
                forceDialogDisplay,
            },
        });
    };

    const onSave = async () => {
        if (WRITE) {
            try {
                if (isCustomFields) {
                    const input = getCustomFieldsInput();
                    saveCustomFields({
                        variables: {
                            ...(accountNumber ? {
                                accountNumber,
                            } : {
                                stockNumber: stockNumber.toString(),
                            }),
                            input,
                        },
                    });
                    return;
                }

                const newData = state[dataFieldName];
                const input = getMapData(title, newData);
                if (title === InventoryPanels.ADVERTISING_SETUP) {
                    const { editVDPReferenceURL, overrideVDPURL } = input;
                    if (editVDPReferenceURL && !StringUtils.isSecureURL(overrideVDPURL)) {
                        ModalUtils.errorMessage(null, 'The Reference VDP url is not valid');
                        return;
                    }

                    delete input.editVDPReferenceURL;
                }

                const variables = {
                    stockNumber,
                    input: {
                        ...input,
                        version,
                    },
                };
                const response = await updateVehicle({ variables });
                if (response?.data?.updateVehicle) {
                    await changeBlockingStatus({
                        variables: {
                            stockNumber,
                            isBlocking: false,
                        },
                    });

                    const updatedFields = dataFieldName === InventoryPanels.PRICING.toLowerCase()
                        ? { ...newData, markup: input.internetPrice - input.cost }
                        : newData;

                    dispatch({
                        type: ACTION_TYPES.INIT_DATA,
                        payload: {
                            fieldName: dataFieldName,
                            data: updatedFields,
                        },
                    });

                    onReload();
                    ModalUtils.successMessage(null, 'Vehicle updated successfully!');
                } else {
                    ModalUtils.errorMessage(null, MessageUtils.getGenericError('save', title));
                }
            } catch (ex) {
                const message = ex.message || MessageUtils.getGenericError('save', title);

                ModalUtils.errorMessage(ex.graphQLErrors, message);
            } finally {
                toggleConfirmDialog(false);
            }
        }
    };

    const onChange = (fieldName, value, additionalData = null) => {
        if (isCustomFields) {
            dispatch({
                type: ACTION_TYPES.UPDATE_CUSTOM_FIELD,
                payload: {
                    index: state.customFields
                        .findIndex((cf) => cf.entityMetadataId === fieldName),
                    value,
                },
            });

            return;
        }

        if (fieldName === 'packField') {
            const { entityMetadataId } = additionalData;
            dispatch({
                type: ACTION_TYPES.UPDATE_PACK_FIELD,
                payload: {
                    entityMetadataId,
                    value,
                },
            });

            return;
        }

        if (additionalData !== null) {
            dispatch({
                type: ACTION_TYPES.ON_CHANGE_FLOORPLAN,
                payload: {
                    dataFieldName,
                    data: additionalData,
                },
            });
        } else if (fieldName === 'packOverride' && !value) {
            dispatch({
                type: ACTION_TYPES.SET_OVERRIDE,
                payload: 0,
            });
        } else {
            dispatch({
                type: ACTION_TYPES.ON_CHANGE,
                payload: {
                    dataFieldName,
                    fieldName,
                    value,
                },
            });
        }
    };

    const updateCustomFieldOptions = (customField) => {
        const { options, value } = customField;
        const isValueIncluded = options.some(
            (option) => option.option === value,
        );

        dispatch({
            type: ACTION_TYPES.UPDATE_CUSTOM_FIELD_OPTIONS,
            payload: {
                index: state.customFields
                    .findIndex((cf) => cf.entityMetadataId === customField.entityMetadataId),
                options,
                value: isValueIncluded ? value : '',
            },
        });
    };

    const checkSave = () => {
        const hasPendingDeals = dealRecords?.some((record) => record.status?.toLowerCase() !== DealStatus.DELETED.toLocaleLowerCase() && !record.postedDate);
        if ([InventoryPanels.PRICING.toLowerCase(), InventoryPanels.FLOORPLAN.toLowerCase()].includes(title?.toLowerCase())
            && hasPendingDeals) {
            toggleConfirmDialog();
        } else {
            onSave();
        }
    };

    const toggleEditMode = (editMode = false) => {
        if (editMode) {
            changeBlockingStatus({
                variables: {
                    stockNumber,
                    isBlocking: true,
                },
            });

            dispatch({
                type: ACTION_TYPES.SET_EDIT_MODE,
            });
            setEditedSection(dataFieldName);
        } else {
            changeBlockingStatus({
                variables: {
                    stockNumber,
                    isBlocking: false,
                },
            });

            dispatch({
                type: ACTION_TYPES.INIT_DATA,
                payload: {
                    fieldName: dataFieldName,
                    data,
                    isCustomFields,
                },
            });
            setEditedSection('');
        }
    };

    const records = getData(title, state);
    const dealPendingCount = state.showConfirmDialog
        ? dealRecords?.filter((record) => record.status?.toLowerCase() !== DealStatus.DELETED.toLowerCase() && record.postedDate === null).length || 0
        : 0;
    const confirmDescription = `This vehicle is currently linked to ${dealPendingCount} active, unposted deal(s). `
    + 'Updating the Pricing or Floorplan data now will trigger a recalculation of '
    + 'these deal records to reflect the new costs. Do you want to proceed with '
    + 'these changes and recalculate the values?';
    return (
        <div className={clsx(classes.root, !isDealsCustomFields ? classes.editPanel : '')}>
            <div className={classes.titleContainer}>
                {isDealsCustomFields && (<div />)}
                {!isDealsCustomFields && (<FormLabel className={classes.title}>{ title }</FormLabel>)}
                {allowEdit && (
                    <GroupActionsButtons
                        onSave={checkSave}
                        toggleEditMode={toggleEditMode}
                        loading={loading || savingCustomFields}
                        editMode={state.editMode}
                        disabled={disabled}
                        allowSave={hasChanges}
                    />
                )}
            </div>
            <>
                {
                    React.Children.map(children, (child) => {
                        if (React.isValidElement(child)) {
                            if (state.editMode && !child.props.readOnly) {
                                return React.cloneElement(child, {
                                    onChange,
                                    updateCustomFieldOptions,
                                    data: records,
                                    writePermission: WRITE,
                                });
                            }
                            if (!state.editMode && child.props.readOnly) {
                                return React.cloneElement(child, {
                                    data: records,
                                });
                            }
                        }

                        return null;
                    })
                }
            </>
            {state.showConfirmDialog && (
                <ConfirmDialog
                    title="Confirm"
                    description={confirmDescription}
                    open={state.showConfirmDialog}
                    variant="outlined"
                    titlePrimary="Yes"
                    titleSecondary="No"
                    onClose={toggleConfirmDialog}
                    onClickSecondary={() => toggleConfirmDialog()}
                    onClickPrimary={onSave}
                    disablePrimaryButton={loading || savingCustomFields}
                />
            )}
        </div>
    );
};

PanelsContainers.propTypes = {
    stockNumber: PropTypes.number,
    accountNumber: PropTypes.number,
    editedSection: PropTypes.string,
    title: PropTypes.string,
    data: PropTypes.object,
    children: PropTypes.node.isRequired,
    setEditedSection: PropTypes.func,
    allowEdit: PropTypes.bool,
    onReload: PropTypes.func,
    blockingStatus: PropTypes.object,
    currentUserId: PropTypes.number,
    changeBlockingStatus: PropTypes.func,
    version: PropTypes.number,
};

PanelsContainers.defaultProps = {
    stockNumber: null,
    accountNumber: null,
    title: 'Vehicle Info',
    data: {},
    allowEdit: false,
    editedSection: '',
    blockingStatus: null,
    currentUserId: 0,
    setEditedSection: () => {},
    onReload: () => {},
    changeBlockingStatus: () => null,
    version: 0,
};

export default PanelsContainers;
